import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaUsers, FaStar, FaCopy, FaEnvelope, FaCheck, FaTimes, FaClock, FaShoppingCart } from 'react-icons/fa';
import { toast } from '../../components/ui/Toast';
import PageHeader from '../../components/PageHeader';
import { FullPageSkeleton } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import {
  usePortalContent,
  getPortalIcon,
  getReferAndEarnPageMeta,
  getReferralStatusLabel,
} from '../../portal';

const ReferAndEarn = () => {
  const { content: portalContent, loading: portalLoading } = usePortalContent('refer-and-earn');
  const meta = useMemo(() => getReferAndEarnPageMeta(portalContent), [portalContent]);
  const PageIcon = getPortalIcon(meta.pageIcon);
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/referrals/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      } else {
        toast.error(meta.toasts.loadError);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error(meta.toasts.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (referralData?.referralLink) {
      navigator.clipboard.writeText(referralData.referralLink);
      setCopied(true);
      toast.success(meta.toasts.linkCopied);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleCopyCode = () => {
    if (referralData?.referralCode) {
      navigator.clipboard.writeText(referralData.referralCode);
      toast.success(meta.toasts.codeCopied);
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();

    if (!inviteForm.email) {
      toast.error(meta.toasts.emailRequired);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteForm.email)) {
      toast.error(meta.toasts.emailInvalid);
      return;
    }

    setInviteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/referrals/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          referredEmail: inviteForm.email,
          referredName: inviteForm.name
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || meta.toasts.inviteSuccess);
        setShowInviteModal(false);
        setInviteForm({ email: '', name: '' });
        fetchReferralData();
      } else {
        toast.error(data.message || meta.toasts.inviteError);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error(meta.toasts.inviteError);
    } finally {
      setInviteLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="w-4 h-4 text-yellow-500" />;
      case 'registered':
        return <FaCheck className="w-4 h-4 text-blue-500" />;
      case 'purchased':
        return <FaShoppingCart className="w-4 h-4 text-green-500" />;
      case 'rewarded':
        return <FaStar className="w-4 h-4 text-primary-500" />;
      default:
        return <FaClock className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading || portalLoading) {
    return (
      <div className="space-y-6">
        <FullPageSkeleton message={meta.loadingMessage} />
      </div>
    );
  }

  return (
    <div className="space-y-6 relative min-h-[60vh]">
      <PageHeader
        icon={PageIcon}
        title={meta.pageTitle}
        subtitle={meta.pageSubtitle}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="card p-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <FaUsers className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-800 dark:text-white">{referralData?.stats?.totalReferrals || 0}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{meta.statsLabels.totalReferrals}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <FaClock className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-800 dark:text-white">{referralData?.stats?.pendingReferrals || 0}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{meta.statsLabels.pending}</p>
            </div>
          </div>
        </div>

        <div className="card p-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <FaCheck className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-800 dark:text-white">{referralData?.stats?.purchasedReferrals || 0}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{meta.statsLabels.successful}</p>
            </div>
          </div>
        </div>

        <div
          className="card p-4 border-0 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          style={{ background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))` }}
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <FaStar className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{referralData?.stats?.availablePoints || 0}</p>
              <p className="text-sm text-white/80">{meta.statsLabels.rewardPoints}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
        <h2 className="text-lg font-semibold text-dark-800 dark:text-white mb-2">{meta.referralLinkSection.title}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{meta.referralLinkSection.description}</p>

        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
            <span className="flex-1 text-sm text-dark-800 dark:text-gray-300 truncate font-mono">{referralData?.referralLink || 'Loading...'}</span>
            <button
              onClick={handleCopyLink}
              className={`p-2 rounded-lg transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'}`}
            >
              {copied ? <FaCheck className="w-4 h-4" /> : <FaCopy className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {meta.referralLinkSection.yourCodePrefix}{' '}
              <strong className="text-primary-600 dark:text-primary-400 font-mono">{referralData?.referralCode || '-'}</strong>
            </span>
            <button onClick={handleCopyCode} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-gray-200 dark:bg-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-dark-500 transition-colors">
              <FaCopy className="w-3 h-3" /> {meta.referralLinkSection.copyCodeLabel}
            </button>
          </div>
        </div>

        <button onClick={() => setShowInviteModal(true)} className="mt-4 flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium hover:shadow-lg hover:shadow-primary-500/30 transition-all">
          <FaEnvelope className="w-4 h-4" /> {meta.referralLinkSection.sendInviteButton}
        </button>
      </div>

      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
        <h2 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">{meta.howItWorks.title}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {meta.howItWorks.steps.map((step) => (
            <div key={step.num} className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 text-center">
              <div className="w-10 h-10 rounded-full bg-primary-500 text-white font-bold flex items-center justify-center mx-auto mb-3">{step.num}</div>
              <h3 className="font-semibold text-dark-800 dark:text-white mb-1">{step.title}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{step.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
          <h2 className="text-lg font-semibold text-dark-800 dark:text-white">{meta.referralsTable.title}</h2>
        </div>
        {referralData?.referrals?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{meta.referralsTable.columns.email}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{meta.referralsTable.columns.name}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{meta.referralsTable.columns.status}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{meta.referralsTable.columns.invitedOn}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{meta.referralsTable.columns.registered}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{meta.referralsTable.columns.reward}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {referralData.referrals.map((referral) => (
                  <tr key={referral.id} className="hover:bg-gray-50 dark:hover:bg-dark-700/50">
                    <td className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">{referral.referredEmail}</td>
                    <td className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">{referral.referredName || referral.referredUser?.name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        referral.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                        referral.status === 'registered' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                        referral.status === 'purchased' || referral.status === 'rewarded' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>
                        {getStatusIcon(referral.status)}
                        {getReferralStatusLabel(meta, referral.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{formatDate(referral.invitationSentAt)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{formatDate(referral.registeredAt)}</td>
                    <td className="px-4 py-3">
                      {referral.status === 'rewarded' || referral.status === 'purchased' ? (
                        <span className="font-semibold text-green-600 dark:text-green-400">{referral.rewardAmount} pts</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={FaUsers}
            description={meta.emptyState.description}
            containerClassName="p-12 text-center"
            iconClassName="w-12 h-12 text-gray-300 dark:text-dark-500 mx-auto mb-3"
            descriptionClassName="text-gray-500 dark:text-gray-400"
          />
        )}
      </div>

      {showInviteModal && createPortal(
        <>
          <div className="fixed inset-0 z-[60] bg-blue-900/30 backdrop-blur-sm pointer-events-none" />
          <div className="fixed inset-0 z-[70]" onClick={() => setShowInviteModal(false)} />
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <FaEnvelope className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-dark-800 dark:text-white">{meta.inviteModal.title}</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{meta.inviteModal.subtitle}</p>
                  </div>
                </div>
                <button onClick={() => setShowInviteModal(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleInviteSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">{meta.inviteModal.emailLabel}</label>
                  <input
                    type="email"
                    placeholder={meta.inviteModal.emailPlaceholder}
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    required
                    className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">{meta.inviteModal.nameLabel}</label>
                  <input
                    type="text"
                    placeholder={meta.inviteModal.namePlaceholder}
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 font-medium hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                  >
                    {meta.inviteModal.cancelLabel}
                  </button>
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium hover:shadow-lg disabled:opacity-50 transition-all"
                  >
                    {inviteLoading ? meta.inviteModal.submittingLabel : meta.inviteModal.submitLabel}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default ReferAndEarn;
